import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { adminApi, errMsg } from "../../lib/api";

const eur = (c) => `€${(c / 100).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" }) : "—");

const exportCsv = (rows) => {
  const head = "code,percent_off,given_to,order,customer,email,date,subtotal,saved,paid";
  const body = rows.map((r) => [r.code, r.percent, `"${(r.given_to || "").replace(/"/g, "'")}"`, r.order, `"${r.customer.replace(/"/g, "'")}"`, r.email, (r.paid_at || "").slice(0, 10), eur(r.subtotal), eur(r.saved), eur(r.total)].join(","));
  const url = URL.createObjectURL(new Blob([[head, ...body].join("\n")], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: "coupon-usage-report.csv" }).click();
  URL.revokeObjectURL(url);
};

const Stat = ({ label, value, id }) => (
  <div className="border-2 border-slate-200 bg-white px-4 py-3" data-testid={id}>
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-display text-xl font-black">{value}</div>
  </div>
);

export const CouponReport = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi().get("/admin/coupons/report").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [refreshKey]);
  if (!data) return null;
  const { rows, by_percent: bp } = data;
  return (
    <div className="mt-8" data-testid="coupon-report">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-black uppercase">Usage report</h3>
        <button type="button" disabled={!rows.length} onClick={() => exportCsv(rows)} className="flex items-center gap-1 text-xs font-bold underline disabled:opacity-40" data-testid="coupon-report-export">
          <Download className="h-3.5 w-3.5" /> Export CSV</button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="Codes redeemed" value={rows.length} id="coupon-report-redeemed" />
        <Stat label="Customers saved" value={eur(data.total_saved)} id="coupon-report-saved" />
        {[10, 20, 50].map((p) => <Stat key={p} label={`${p}% codes`} value={`${bp[p].used} · ${eur(bp[p].saved)}`} id={`coupon-report-pct-${p}`} />)}
      </div>
      {rows.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="coupon-report-empty">No codes have been used on a paid order yet.</p> : (
        <div className="mt-3 max-h-72 overflow-auto border-2 border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-left text-xs" data-testid="coupon-report-table">
            <thead className="sticky top-0 bg-slate-100 uppercase"><tr>{["Code", "Given to", "Order", "Customer", "Date", "Shields", "Saved", "Paid"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.order} className="border-t border-slate-100" data-testid={`coupon-report-row-${r.code}`}>
                <td className="px-3 py-1.5 font-mono font-bold">{r.code} <span className="font-sans text-slate-400">{r.percent}%</span></td>
                <td className="px-3 py-1.5">{r.given_to || "—"}</td>
                <td className="px-3 py-1.5 font-bold">{r.order}</td>
                <td className="px-3 py-1.5">{r.customer}<div className="text-slate-400">{r.email}</div></td>
                <td className="px-3 py-1.5">{day(r.paid_at)}</td>
                <td className="px-3 py-1.5">{eur(r.subtotal)}</td>
                <td className="px-3 py-1.5 font-bold text-lime-700">−{eur(r.saved)}</td>
                <td className="px-3 py-1.5 font-bold">{eur(r.total)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
